import { clsx } from 'clsx';
import { Icon } from './ui/Icon';
import { MediaImport } from './MediaImport';
import { useEditor } from '@/store/editor';
import type { Asset } from '@/lib/api';

const sceneGradients = [
  'from-[#F87B4F] to-[#8B4A52]',
  'from-[#FDB87D] to-[#4D2F3E]',
  'from-[#3A5F7F] to-[#1E2A4A]',
  'from-[#5A4A6F] to-[#2A2A4A]',
  'from-[#1A1A1E] to-black',
  'from-ai to-accent',
];

type Props = {
  projectId: string | null;
  assets: Asset[];
};

export function Storyboard({ projectId, assets }: Props) {
  const demoScenes = useEditor((s) => s.scenes);
  const activeSceneId = useEditor((s) => s.activeSceneId);
  const setActiveSceneId = useEditor((s) => s.setActiveSceneId);
  const openGenerate = useEditor((s) => s.openGenerate);
  const setToast = useEditor((s) => s.setToast);

  // Real assets if available, otherwise hardcoded demo scenes
  const useRealAssets = projectId !== null && assets.length > 0;
  const totalSec = useRealAssets
    ? assets.reduce((sum, a) => sum + (a.duration_ms ?? 0), 0) / 1000
    : 58;

  return (
    <footer className="bg-bg-panel border-t border-border flex flex-col px-4 py-3 gap-2.5">
      <div className="flex items-center justify-between">
        <div className="font-semibold text-[13px] flex items-center gap-2">
          <Icon name="layout-grid" size={14} />
          Storyboard
          <span className="text-text-muted font-normal text-xs pl-1">
            {useRealAssets
              ? `${assets.length} klipp${totalSec > 0 ? ` . ${Math.round(totalSec)}s` : ''}`
              : `${demoScenes.length} scener . ${totalSec} sekunder`}
          </span>
        </div>
        <div className="flex gap-1 items-center">
          {projectId && <MediaImport projectId={projectId} />}
          <button className="btn btn-ghost" title="Ljudspar">
            <Icon name="music" size={14} />
          </button>
          <button className="btn btn-ghost" title="Textremsor">
            <Icon name="captions" size={14} />
          </button>
          <div className="w-px bg-border mx-1.5" />
          <button className="btn btn-ghost" title="Zooma in">
            <Icon name="zoom-in" size={14} />
          </button>
          <button className="btn btn-ghost" title="Zooma ut">
            <Icon name="zoom-out" size={14} />
          </button>
        </div>
      </div>

      <div className="flex gap-2.5 overflow-x-auto py-1 pb-2">
        {useRealAssets
          ? assets.map((a, idx) => (
              <AssetCard
                key={a.id}
                asset={a}
                gradient={sceneGradients[idx % sceneGradients.length]}
                active={false}
                onClick={() => setToast(`Visar ${a.original_name ?? 'klipp'}`)}
              />
            ))
          : demoScenes.map((scene, idx) => (
              <button
                key={scene.id}
                onClick={() => {
                  setActiveSceneId(scene.id);
                  setToast(`Zoomar in pa ${scene.name}`);
                }}
                className={clsx(
                  'flex-shrink-0 w-[170px] bg-bg-elevated border-2 rounded-[10px] overflow-hidden transition-all hover:-translate-y-0.5',
                  activeSceneId === scene.id
                    ? 'border-accent shadow-[0_0_0_3px_rgba(59,130,246,0.15),_0_4px_16px_rgba(59,130,246,0.2)]'
                    : 'border-transparent hover:border-border-strong',
                )}
              >
                <div
                  className={`h-[95px] relative flex items-center justify-center text-white/50 bg-gradient-to-br ${sceneGradients[idx] ?? 'from-gray-700 to-gray-900'}`}
                >
                  {scene.aiBadge && (
                    <div className="absolute top-1.5 right-1.5 bg-ai/95 text-white px-1.5 py-0.5 rounded text-[10px] font-semibold flex items-center gap-0.5">
                      <Icon name="sparkles" size={14} />
                      AI
                    </div>
                  )}
                  <Icon name={scene.icon} size={20} />
                </div>
                <div className="px-2.5 py-2 flex justify-between items-center text-[11px] text-text-muted">
                  <span className="text-text font-medium truncate">{scene.name}</span>
                  <span>{scene.duration}</span>
                </div>
              </button>
            ))}
        <button
          onClick={openGenerate}
          className="flex-shrink-0 w-[110px] bg-transparent border-2 border-dashed border-border-strong rounded-[10px] flex flex-col items-center justify-center text-text-muted gap-1.5 text-xs hover:border-accent hover:text-accent hover:bg-[rgba(59,130,246,0.15)] transition-all"
        >
          <Icon name="plus" size={20} />
          <span>Ny scen</span>
        </button>
      </div>
    </footer>
  );
}

function AssetCard({
  asset,
  gradient,
  active,
  onClick,
}: {
  asset: Asset;
  gradient: string;
  active: boolean;
  onClick: () => void;
}) {
  const icon = asset.kind === 'video' ? 'film' : asset.kind === 'audio' ? 'music' : asset.kind === 'image' ? 'image' : 'sparkles';
  return (
    <button
      onClick={onClick}
      className={clsx(
        'flex-shrink-0 w-[170px] bg-bg-elevated border-2 rounded-[10px] overflow-hidden transition-all hover:-translate-y-0.5',
        active
          ? 'border-accent shadow-[0_0_0_3px_rgba(59,130,246,0.15),_0_4px_16px_rgba(59,130,246,0.2)]'
          : 'border-transparent hover:border-border-strong',
      )}
    >
      <div className={`h-[95px] relative flex items-center justify-center text-white/50 bg-gradient-to-br ${gradient}`}>
        <Icon name={icon} size={20} />
      </div>
      <div className="px-2.5 py-2 flex justify-between items-center text-[11px] text-text-muted">
        <span className="text-text font-medium truncate">{asset.original_name ?? asset.id.slice(0, 8)}</span>
        <span className="capitalize">{asset.kind}</span>
      </div>
    </button>
  );
}
